import React, { useState, useRef, useEffect } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'

import IncrementalRevenue from "@/assets/risk-reward/barChart.png"
import ReduceOpsCost from "@/assets/risk-reward/revenue.png"
import ConsolidatedSolution from "@/assets/risk-reward/flowChart.png"
import EasyClaim from "@/assets/risk-reward/heart.png"
import BrandLoyalty from "@/assets/risk-reward/shoppingCart.png"
import Analytics from "@/assets/risk-reward/dotChart.png"
import APIReference from "@/assets/risk-reward/apiReference.png"

import ShopifyLogo from "@/assets/logos/Shopify.png"
import SalesForceLogo from "@/assets/logos/Salesforce.png"
import MagentoLogo from "@/assets/logos/Magento.png"
import OracleLogo from "@/assets/logos/Oracle.png"
import BigCommerceLogo from "@/assets/logos/BigCommerce.png"
import { Link } from 'react-router-dom'

const features = [
  {
    icon: IncrementalRevenue,
    title: 'Incremental Revenue',
    desc: 'Merchants receive a portion of each Protega protection plan sale, which goes straight to your bottom line.'
  },
  {
    icon: ReduceOpsCost,
    title: 'Reduce Operational Costs',
    desc: 'Automate processing from intake through fulfillment without manual review of every customer claim.'
  },
  {
    icon: ConsolidatedSolution,
    title: 'Consolidated Solution',
    desc: 'Post-purchase fraud detection, automated claim support, order tracking, returns and exchanges — all in one place.'
  },
  {
    icon: EasyClaim,
    title: 'Easy Claims, Happy Customers',
    desc: 'Simple claim filing, with over 98% of Protega claims being handled in 90 seconds or less, turning frowns upside down.'
  },
  {
    icon: BrandLoyalty,
    title: 'Brand Loyalty',
    desc: 'Turn potential detractors into long-term brand advocates and repeat buyers with fast and easy claim resolution.'
  },
  {
    icon: Analytics,
    title: 'Analytics & Optimization',
    desc: 'Understand performance while our data scientists and marketing experts optimize Protega offers to drive more revenue.'
  }
]

const messages = [
  <>Minimize liability and risk while <span className="tw-text-[#00C5FB]">reducing post-purchase fraud.</span></>,
  <>Streamline claim resolution with <span className="tw-text-[#00C5FB]">98% processed in 90 seconds or less.</span></>,
  <>Drive an average <span className="tw-text-[#00C5FB]">+61% lift</span> in customer lifetime value.</>
]

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
}

const RiskReward: React.FC = () => {
  const gridRef = useRef<HTMLDivElement | null>(null)
  const heroRef = useRef(null)
  const observerRefs = useRef<(HTMLDivElement | null)[]>([])

  const [ctaHovered, setCtaHovered] = useState(false)
  const [activeMsg, setActiveMsg] = useState(0)

  const heroInView = useInView(heroRef, { once: true, margin: '-100px' })

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    observerRefs.current.forEach((el, i) => {
      if (!el) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveMsg(i)
        },
        { threshold: 0.5 }
      )
      observer.observe(el)
      observers.push(observer)
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [])

  return (
    <section className="tw-bg-black tw-px-6 md:tw-px-12">
      <motion.div
        ref={heroRef}
        initial={{ opacity: 0, y: 40 }}
        animate={heroInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="tw-min-h-[60vh] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4 md:tw-px-8 lg:tw-px-16"
      >
        <h2 className="tw-text-[40px] md:tw-text-[64px] lg:tw-text-[80px] tw-font-extrabold tw-text-white tw-leading-[1.1] tw-mb-6 md:tw-mb-6">
          Lower risk.<br />
          <span className="tw-text-[#00C5FB]">Greater reward.</span>
        </h2>
        <p className="tw-text-white tw-text-[20px] md:tw-text-[28px] lg:tw-text-[32px] tw-font-medium tw-max-w-3xl tw-leading-[1.3]">
          Maximize loyalty with positive customer<br className="tw-hidden md:tw-block" />
          experiences that weed out the bad actors.
        </p>
      </motion.div>

      <div className="tw-relative tw-h-[400vh]">
        <div className="tw-sticky tw-top-0 tw-h-screen tw-flex tw-items-center tw-justify-center tw-bg-black">
          <AnimatePresence mode="wait">
            <motion.p
              key={activeMsg}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="tw-text-white tw-text-[28px] md:tw-text-[40px] lg:tw-text-[54px] tw-font-bold tw-text-center tw-max-w-4xl tw-leading-[1.2] tw-px-4"
            >
              {messages[activeMsg]}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="tw-space-y-[100vh]">
          {messages.map((_, i) => (
            <div
              key={i}
              className="tw-h-[1px]"
              ref={(el) => { observerRefs.current[i] = el }}
            />
          ))}
          <div className="tw-h-screen" />
        </div>
      </div>

      {/* ─── SEQUENTIAL GRID ANIMATION ─────────────────────────────────────── */}
      <motion.div
        ref={gridRef}
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="tw-max-w-5xl tw-mx-auto tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mt-20 tw-mb-20 tw-h-full"
      >
        {features.map((f, i) => (
          <motion.div
            key={i}
            variants={itemVariants}
            className={clsx(
              'tw-rounded-2xl tw-p-6 tw-transition tw-duration-500 tw-ease-in-out tw-bg-[#111111] tw-h-full',
              ctaHovered && i === 4 && 'tw-bg-gradient-to-br tw-from-[#111111] tw-to-[rgba(22,78,99,0.2)]',
              ctaHovered && i === 5 && 'tw-bg-gradient-to-b tw-from-[#111111] tw-to-[rgba(22,78,99,0.3)]'
            )}
          >
            <img
              src={f.icon}
              alt={`${f.title} icon`}
              className="tw-w-10 tw-h-10 tw-mb-4 tw-object-contain"
            />
            <h4 className="tw-text-[#00C5FB] tw-text-lg tw-font-semibold tw-mb-2">
              {f.title}
            </h4>
            <p className="tw-text-gray-300 tw-text-base tw-leading-relaxed">
              {f.desc}
            </p>
          </motion.div>
        ))}

        <motion.div variants={itemVariants} className={clsx(
          'tw-rounded-2xl tw-p-6 tw-transition tw-duration-500 tw-ease-in-out md:tw-col-span-2 tw-flex tw-flex-col tw-justify-between tw-h-full',
          ctaHovered
            ? 'tw-bg-gradient-to-r tw-from-[#111111] tw-to-[rgba(22,78,99,0.3)]'
            : 'tw-bg-[#111111]'
        )}>
          <div>
            <img
              src={APIReference}
              alt="API icon"
              className="tw-w-10 tw-h-10 tw-mb-4 tw-object-contain"
            />
            <h4 className="tw-text-[#00C5FB] tw-text-lg tw-font-semibold tw-mb-2">
              We make it easy to get started.
            </h4>
            <p className="tw-text-gray-300 tw-text-sm tw-leading-relaxed tw-mb-6">
              Our platform was built to enable any merchant to offer Protega. Seamlessly integrate using our robust APIs or commerce platform integrations like Shopify, BigCommerce, and more. Our tech will then optimize your programs to maximize conversion rates and revenue while lowering costs.
            </p>
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
            {[ShopifyLogo, SalesForceLogo, MagentoLogo, OracleLogo, BigCommerceLogo].map((logo, i) => (
              <img
                key={i}
                src={logo}
                alt={`Logo ${i + 1}`}
                className="tw-h-6 tw-object-contain"
              />
            ))}
          </div>
        </motion.div>

        <motion.div
  variants={itemVariants}
  onPointerEnter={() => {
    if (window.innerWidth >= 768) setCtaHovered(true)
  }}
  onPointerLeave={() => {
    if (window.innerWidth >= 768) setCtaHovered(false)
  }}
  className="tw-bg-cyan-900/50 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-justify-between tw-text-white tw-transition tw-duration-300 tw-ease-in-out tw-min-h-[250px] tw-h-full"
>

  <div className="tw-flex-1">
    <h4 className="tw-font-semibold tw-text-lg tw-leading-snug tw-text-cyan-300 tw-mb-6">
      No dev team? <br /> No problem. <br /> We'll get you set up in no time.
    </h4>
  </div>
  
  <Link
    to="/schedule-demo"
    className="tw-bg-[#00C5FB] tw-text-black tw-font-semibold tw-text-sm tw-py-2.5 tw-px-5 tw-rounded-full hover:tw-bg-white hover:tw-text-cyan-600 tw-transition-colors tw-duration-700 tw-text-center"
  >
    Get in Touch
  </Link>
</motion.div>

      </motion.div>
    </section>
  )
}

export default RiskReward
